import type { MediaImage } from '@efdigital/ll-addon-data';
import { debounce } from '@efdigital/ll-addon-ui/modifiers/debounce';
import type { MediaImageInfo } from '@efdigital/ll-admin-v2/lib/media/media-image-info';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';

import type { MediaImageSizeType } from '../../lib/media/models.ts';
import { type ImageContent, type SizePreset } from '../../lib/media/sizes.ts';
import type MediaImagesService from '../../services/media-images.ts';
import { ImagePickerModal } from './image-picker/image-picker-modal.gts';

export interface ImagePickerSignature {
  Element: HTMLDivElement;
  Args: {
    /**
     * The current image URL value
     */
    value: string;
    /**
     * Callback when the value changes
     */
    onChange: (url: string, mediaImage?: MediaImage) => void;
    /**
     * Size hint for the image picker (e.g., 'cohort-icon', 'page-banner')
     */
    sizeHint: MediaImageSizeType | 'original';

    /**
     * Optional custom size for the image picker (e.g., { width: 300, height: 300 })
     */
    customSize?: SizePreset;
    /**
     * Optional title to pre-populate when creating a new image
     */
    defaultTitle?: string;
    /**
     * Context title for AI-powered image suggestions (e.g., cohort name, module name)
     */
    contextTitle?: string;
    /**
     * Whether the input is readonly
     */
    readonly?: boolean;
    /**
     * Optional ID for the input element
     */
    id?: string;
    /**
     * Optional placeholder text
     */
    placeholder?: string;
    /**
     * Whether the image is required
     */
    required: boolean;
  };
}

export default class ImagePicker extends Component<ImagePickerSignature> {
  @service('media-images') declare mediaImages: MediaImagesService;
  @service declare store: Store;

  mediaImageInfo = (): MediaImageInfo | null => {
    return this.mediaImages.lookupMediaImageInfo(this.args.value);
  };

  updateValue = (event: Event): void => {
    const input = event.target as HTMLInputElement;
    this.args.onChange(input.value);
  };

  clear = (): void => {
    this.args.onChange('');
  };

  imageSelected = (
    imageContent: ImageContent | null,
    mediaImage?: MediaImage
  ): void => {
    this.args.onChange(imageContent?.url || '', mediaImage);
  };

  get thumbnailStyle() {
    return htmlSafe('max-height: 38px; max-width: 60px;');
  }

  <template>
    <div class="image-editor" ...attributes>
      {{#if @readonly}}
        {{! Readonly mode - just show the image and URL }}
        {{#if @value}}
          <div class="input-group">
            {{#let (this.mediaImageInfo) as |mediaInfo|}}
              <span class="input-group-text p-1">
                {{#if mediaInfo.record}}
                  <a
                    href={{mediaInfo.content.url}}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="d-block"
                  >
                    <img
                      src={{mediaInfo.record.thumbnailUrl}}
                      alt={{mediaInfo.record.alt}}
                      title={{mediaInfo.record.title}}
                      class="img-thumbnail"
                      style={{this.thumbnailStyle}}
                    />
                  </a>
                {{else}}
                  <a
                    href={{@value}}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="d-block"
                  >
                    <img
                      src={{@value}}
                      alt="Preview"
                      class="img-thumbnail"
                      style={{this.thumbnailStyle}}
                    />
                  </a>
                {{/if}}
              </span>
            {{/let}}
            <input
              type="text"
              class="form-control"
              value={{@value}}
              id={{@id}}
              placeholder={{@placeholder}}
              readonly
            />
          </div>
        {{else}}
          <input
            type="text"
            class="form-control"
            value=""
            id={{@id}}
            placeholder={{@placeholder}}
            readonly
          />
        {{/if}}
      {{else}}
        {{! Editable mode - show thumbnail, input, and change button }}
        <div class="input-group">
          {{#if @value}}
            {{#let (this.mediaImageInfo) as |mediaInfo|}}
              <span class="input-group-text p-1">
                {{#if mediaInfo.loading}}
                  <div class="spinner-border spinner-border-sm" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                {{else if mediaInfo.record}}
                  <a
                    href={{mediaInfo.content.url}}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="d-block"
                  >
                    <img
                      src={{mediaInfo.record.thumbnailUrl}}
                      alt={{mediaInfo.record.alt}}
                      title={{mediaInfo.record.title}}
                      class="img-thumbnail"
                      style={{this.thumbnailStyle}}
                    />
                  </a>
                {{else}}
                  <a
                    href={{@value}}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="d-block"
                  >
                    <img
                      src={{@value}}
                      alt="Preview"
                      class="img-thumbnail"
                      style={{this.thumbnailStyle}}
                    />
                  </a>
                {{/if}}
              </span>
            {{/let}}
          {{/if}}
          <input
            type="text"
            class="form-control"
            value={{@value}}
            id={{@id}}
            placeholder={{@placeholder}}
            {{debounce this.updateValue}}
          />
          <ImagePickerModal
            @onSelectImage={{this.imageSelected}}
            @requiredSize={{@sizeHint}}
            @customSize={{@customSize}}
          >
            <:input as |showModal|>
              <button
                type="button"
                class="btn btn-primary"
                {{on "click" showModal}}
              >
                Change...
              </button>
            </:input>
          </ImagePickerModal>

        </div>
      {{/if}}
    </div>
  </template>
}
